import Cookies from 'js-cookie'
import useAdvancedSearch from '../../hooks/use-advanced-search'
import useUser from '../../hooks/use-user'
import { isValidToken } from '../../services/auth-utils'
import { useEffect, useState } from 'react'
import { AdvancedSearchTable, ExportConfigProps } from '../aoraki/generic-search-react/src'
import { getRouteForWellStatus } from '../../routes/utils/route-utils'
import { DISPLAY_DATE_FORMAT, SearchTypeKey, WELLS_DEFAULT_SEARCH_RESULTS_SIZE } from '../../services/constants'
import { useNavigate } from 'react-router-dom'
import { InlineNotification, LoadingIndicator, useTitleChange } from '@msaf/core-react'
import { getFormattedDateTimeForExport } from '../../services/export-utils'

interface SearchProps {
  searchKey: SearchTypeKey
}

export default function Search({ searchKey = SearchTypeKey.NATIONAL }: SearchProps) {
  useTitleChange('Wells Aotearoa NZ | Search')
  const [exportConfig, setExportConfig] = useState<Array<ExportConfigProps>>()
  const navigate = useNavigate()

  const { user } = useUser()

  const { onSearchQueryChange, searchTypeKey, searchTemplate, userPermissions, requestOptions, cachedSearchQuery } =
    useAdvancedSearch({ searchTypeKey: searchKey })

  useEffect(() => {
    const localDateTimeString = getFormattedDateTimeForExport()

    const exportConfig = [
      {
        id: 'wellSummaryExport',
        url: `wells/csv-export`,
        csrfToken: Cookies.get('csrftoken'),
        label: 'Export well summary',
        viewKey: 'wellSummaryExport',
        exportFileName: `wellSummaryExport-${localDateTimeString}.csv`,
      },
    ]
    if (isValidToken(user?.token)) {
      exportConfig.push({
        id: 'wellsStrataExport',
        url: `wells/csv-export`,
        csrfToken: Cookies.get('csrftoken'),
        label: 'Export well strata',
        viewKey: 'wellsStrataExport',
        exportFileName: `wellStrataExport-${localDateTimeString}.csv`,
      })
    }
    setExportConfig(exportConfig)
  }, [user?.token, requestOptions.urlPrefix])

  if (!searchTemplate) {
    return <LoadingIndicator>Loading&hellip;</LoadingIndicator>
  }

  const dynamicTransitionAction = (id: number, wellRecordStatus: string) => {
    const path = getRouteForWellStatus(user, id, wellRecordStatus)
    navigate(path)
  }

  return (
    <AdvancedSearchTable
      searchTypeKey={searchTypeKey}
      requestOptions={{
        ...requestOptions,
        urlPrefix: `${requestOptions.urlPrefix}/generic-search`,
      }}
      dateFormat={DISPLAY_DATE_FORMAT}
      searchTemplate={searchTemplate}
      pageSize={WELLS_DEFAULT_SEARCH_RESULTS_SIZE}
      currentUserPermissions={userPermissions}
      customActions={{ dynamicTransitionAction }}
      noResultsComponent={
        <InlineNotification messageType='info' isDismissable={false}>
          No well records available.
        </InlineNotification>
      }
      defaultSortBy={{
        orderColumn: 'councilWellNo',
      }}
      isShowResultsCard={false}
      isShowFiltersCard={false}
      isTabbedContent={false}
      exportConfig={exportConfig}
      onSearchQueryChange={onSearchQueryChange}
      cachedSearchQuery={cachedSearchQuery}
    />
  )
}
